import React, { useCallback, useEffect, useRef, useState } from "react";
import SheetGround from "../ui/SheetGround";
import { VELLUM as T, SANS, MONO, display, mono, TitleBlock } from "./kit";

/* A long document, drawn as a folio.

   The reading measure on the left, the apparatus on the right, one rule
   between them that the reader's position descends. On a legal notice the
   margin is doing real work rather than decoration: fourteen cross-referenced
   sections want a contents list that stays in view and says where you are.

   The prose inside is plain JSX - h2, p, ul, li, strong, a, table - styled
   from here. Legal copy is edited by people who should not have to touch a
   style object, and text that is never retyped is text that cannot drift. */

export const DP = "doc";

/* The line, below the site header, at which a heading counts as the one
   being read. */
const READING_LINE = 120;

export interface DocumentSheetProps {
  title: string;
  code: string;
  dateline: string;
  /* [anchor id, heading exactly as it appears in the prose] */
  sections: [string, string][];
  blockRows?: [string, string][];
  children: React.ReactNode;
}

/* Which section is being read.

   Deliberately not a scroll listener: an IntersectionObserver wakes only when
   a heading actually crosses the viewport, and the callback then measures all
   of them to pick the last one above the reading line. Measuring on the
   crossing rather than trusting which entry fired keeps it exact when a jump
   skips several headings at once. */
function useReadingSection(sections: [string, string][]): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const ids = sections.map(([id]) => id);

    const pick = () => {
      let current: string | null = null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= READING_LINE) current = id;
        else break;
      }
      /* Above the first heading, the first section is the one in hand. */
      setActive(current ?? ids[0] ?? null);
    };

    const io = new IntersectionObserver(pick, { threshold: [0, 1] });
    const seen: HTMLElement[] = [];
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) {
        io.observe(el);
        seen.push(el);
      }
    }
    pick();

    window.addEventListener("resize", pick);
    return () => {
      io.disconnect();
      window.removeEventListener("resize", pick);
      seen.length = 0;
    };
  }, [sections]);

  return active;
}

export const DocumentSheet: React.FC<DocumentSheetProps> = ({
  title,
  code,
  dateline,
  sections,
  blockRows,
  children,
}) => {
  const active = useReadingSection(sections);
  const tocRef = useRef<HTMLOListElement>(null);

  /* When the contents is taller than the pane it scrolls itself, so the row
     for the section in hand is brought into the pane - and only that pane,
     never the page. */
  useEffect(() => {
    const list = tocRef.current;
    if (!list || !active) return;
    const row = list.querySelector<HTMLElement>(`[data-toc="${active}"]`);
    if (!row) return;
    const pane = list.parentElement?.parentElement;
    if (!pane || pane.scrollHeight <= pane.clientHeight) return;
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < pane.scrollTop) pane.scrollTop = top - 8;
    else if (bottom > pane.scrollTop + pane.clientHeight) pane.scrollTop = bottom - pane.clientHeight + 8;
  }, [active]);

  /* The heading is taken to the reading line. scroll-margin-top on the
     heading keeps it clear of the site header. The href stays real so the
     row can still be middle-clicked or copied. */
  const goTo = useCallback((e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  }, []);

  return (
    <SheetGround className={`${DP}-root`} style={{ fontFamily: SANS, color: T.ink }}>
      <div
        className={`${DP}-grid`}
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "0 20px",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1px 296px",
          columnGap: 40,
          alignItems: "start",
        }}
      >
        <div
          className={`${DP}-spine`}
          aria-hidden
          style={{ gridColumn: 2, gridRow: "1 / -1", alignSelf: "stretch", position: "relative", width: 1, background: T.rule }}
        >
          <span
            className={`${DP}-spine-fill`}
            style={{
              position: "absolute",
              left: -1.5,
              top: 0,
              width: 4,
              height: "100%",
              background: T.accent,
              transformOrigin: "top center",
              transform: "scaleY(0)",
            }}
          />
        </div>

        <article className={`${DP}-measure`} style={{ gridColumn: 1, paddingTop: 44, paddingBottom: 80 }}>
          <div
            style={{
              ...mono(T),
              borderBottom: `1px solid ${T.rule}`,
              paddingBottom: 12,
              marginBottom: 30,
              display: "flex",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <span>{code}</span>
            <span>{dateline}</span>
          </div>

          <h1 style={{ ...display(T, "clamp(34px, 5.2vw, 62px)"), lineHeight: 0.98, letterSpacing: "-0.04em" }}>
            {title}
          </h1>

          <div className={`${DP}-prose`}>{children}</div>
        </article>

        {/* The aside must stretch to the row, or the sticky child inside it
            has no height to travel through and never pins. */}
        <aside
          className={`${DP}-margin`}
          style={{ gridColumn: 3, alignSelf: "stretch", paddingTop: 44, paddingBottom: 80 }}
        >
          <div className={`${DP}-sticky`}>
            {blockRows && blockRows.length > 0 && <TitleBlock t={T} rows={blockRows} />}

            <nav aria-label="Contents" style={{ marginTop: blockRows ? 22 : 0 }}>
              <div
                style={{
                  ...mono(T, T.accent),
                  paddingBottom: 10,
                  borderBottom: `1.5px solid ${T.ink}`,
                  background: T.ground,
                }}
              >
                § contents
              </div>
              <ol ref={tocRef} style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {sections.map(([id, label]) => {
                  const on = id === active;
                  return (
                    <li key={id} data-toc={id} style={{ borderBottom: `1px solid ${T.rule}` }}>
                      <a
                        href={`#${id}`}
                        onClick={(e) => goTo(e, id)}
                        aria-current={on ? "true" : undefined}
                        className={`${DP}-toc`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "14px minmax(0,1fr)",
                          gap: 8,
                          alignItems: "start",
                          padding: "9px 0",
                          textDecoration: "none",
                          fontSize: 13,
                          lineHeight: 1.35,
                          color: on ? T.ink : T.mute,
                          fontWeight: on ? 600 : 400,
                        }}
                      >
                        {/* A registration mark on the section in hand. */}
                        <span
                          aria-hidden
                          style={{
                            width: on ? 7 : 9,
                            height: on ? 7 : 1,
                            marginTop: on ? 6 : 9,
                            background: on ? T.accent : T.rule,
                          }}
                        />
                        <span>{label}</span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
        </aside>
      </div>

      <style>{`
        .${DP}-sticky {
          position: sticky;
          top: 84px;
          max-height: calc(100vh - 108px);
          overflow-y: auto;
          /* The ground continues behind it, so the prose cannot show through
             where the pane overlaps it. */
          background: ${T.ground};
        }

        /* Reading progress, taken from the compositor rather than a listener. */
        @keyframes ${DP}-ink-rule { from { transform: scaleY(0); } to { transform: scaleY(1); } }
        @supports (animation-timeline: view()) {
          @media (prefers-reduced-motion: no-preference) {
            .${DP}-spine-fill {
              animation: ${DP}-ink-rule linear both;
              animation-timeline: view(block);
              animation-range: cover 0% cover 100%;
            }
          }
        }

        .${DP}-toc { transition: color 140ms ease; }
        .${DP}-toc:hover { color: ${T.accent}; }
        .${DP}-toc:focus-visible,
        .${DP}-prose a:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 3px; }

        /* ── The document ──────────────────────────────────────────────── */

        .${DP}-prose {
          font-family: ${SANS};
          font-size: 16.5px;
          line-height: 1.62;
          color: ${T.mute};
          /* The title sets its own margin to zero, so the gap under it
             belongs to the document. */
          margin-top: 34px;
        }

        /* A document may hang its anchor on the heading (GDPR) or on a
           section wrapping it (Terms). Both must clear the site header. */
        .${DP}-prose section { scroll-margin-top: 92px; }
        .${DP}-prose section > h2:first-child { margin-top: 0; }
        .${DP}-prose section + section > h2:first-child { margin-top: 56px; }

        .${DP}-prose h2 {
          font-family: ${SANS};
          font-weight: 700;
          font-size: clamp(21px, 2.6vw, 28px);
          line-height: 1.1;
          letter-spacing: -0.03em;
          color: ${T.ink};
          border-bottom: 1.5px solid ${T.ink};
          padding-bottom: 11px;
          /* More space above a heading than below it. */
          margin: 56px 0 22px;
          scroll-margin-top: 92px;
        }

        /* A subheading inside a section. It takes weight rather than a rule:
           the heavy rule is what marks a section, and giving one to both
           would flatten the two levels into each other. */
        .${DP}-prose h3 {
          font-family: ${SANS};
          font-weight: 700;
          font-size: 17.5px;
          line-height: 1.25;
          letter-spacing: -0.02em;
          color: ${T.ink};
          margin: 34px 0 12px;
          scroll-margin-top: 92px;
        }

        .${DP}-prose p {
          margin: 0 0 18px;
          /* A legal clause is still prose: hold a readable line. */
          max-width: 72ch;
        }

        .${DP}-prose strong { color: ${T.ink}; font-weight: 600; }
        .${DP}-prose em { font-style: italic; }

        .${DP}-prose a {
          color: ${T.accent};
          text-decoration: underline;
          text-underline-offset: 3px;
        }
        .${DP}-prose a:hover { color: ${T.accentDeep}; }

        /* Ruled rows rather than bullets: the same schedule the sheet uses
           everywhere else, with a drawn tick for the marker. */
        .${DP}-prose ul {
          list-style: none;
          margin: 0 0 20px;
          padding: 0;
          max-width: 78ch;
        }
        .${DP}-prose ul > li {
          position: relative;
          border-top: 1px solid ${T.rule};
          padding: 13px 0 13px 24px;
        }
        .${DP}-prose ul > li:last-child { border-bottom: 1px solid ${T.rule}; }
        .${DP}-prose ul > li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 24px;
          width: 10px;
          height: 1px;
          background: ${T.accent};
        }

        /* ── Schedules ─────────────────────────────────────────────────── */

        .${DP}-table-wrap { overflow-x: auto; margin: 0 0 22px; }
        .${DP}-prose table {
          width: 100%;
          min-width: 640px;
          border-collapse: collapse;
        }
        .${DP}-prose thead th {
          font-family: ${MONO};
          font-size: 10.5px;
          font-weight: 500;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: ${T.mute};
          text-align: left;
          padding: 0 12px 10px 0;
          border-bottom: 1.5px solid ${T.ink};
          vertical-align: bottom;
        }
        .${DP}-prose tbody td {
          padding: 14px 12px 14px 0;
          border-bottom: 1px solid ${T.rule};
          font-size: 14px;
          line-height: 1.5;
          vertical-align: top;
        }
        .${DP}-prose tbody td:last-child,
        .${DP}-prose thead th:last-child { padding-right: 0; }
        .${DP}-prose tbody td strong {
          font-family: ${MONO};
          font-weight: 500;
          font-size: 13px;
          color: ${T.ink};
        }

        @media (max-width: 980px) {
          .${DP}-grid { grid-template-columns: 1px minmax(0, 1fr) !important; column-gap: 0 !important; }
          .${DP}-spine { grid-column: 1 !important; }
          .${DP}-measure, .${DP}-margin { grid-column: 2 !important; padding-left: 22px !important; }
          /* Contents first: on a phone it is navigation, not an annotation,
             and pinning a list this long would eat the screen. */
          .${DP}-margin { grid-row: 1; padding-top: 34px !important; padding-bottom: 0 !important; }
          .${DP}-measure { grid-row: 2; padding-top: 30px !important; }
          .${DP}-sticky { position: static !important; max-height: none !important; overflow: visible !important; }
        }

        @media (prefers-reduced-motion: reduce) {
          .${DP}-toc { transition: none; }
        }
      `}</style>
    </SheetGround>
  );
};
